import * as React from "react"
import {
  Elements,
  CardElement,
  useElements,
  useStripe
} from "@stripe/react-stripe-js"
import moment from "moment"
import { getStripe } from "../utils/stripe"

const CardElementOptions = {
  iconStyle: "solid" as const,
  style: {
    base: {
      iconColor: '#c4f0ff',
      color: '#262626',
      fontWeight: '500',
      fontFamily: 'Roboto, Open Sans, Segoe UI, sans-serif',
      fontSize: '16px',
      fontSmoothing: 'antialiased',
      ':-webkit-autofill': {
        color: '#fce883',
      },
      '::placeholder': {
        color: '#87BBFD',
      },
    },
    invalid: {
      iconColor: '#FFC7EE',
      color: '#FFC7EE',
    },
  },
}

interface CardFormProps {
  confirm: boolean
  orders: any[]
  isTrial: boolean
  current_user: any
  plans: any[]
  csrf_token: string
  use_existing: string
  existing_payment_methods: any[]
  use_payment_intent: boolean
  data: any
  purchase_amount: number
}

export default function CardForm({
  confirm,
  orders,
  isTrial,
  current_user,
  plans,
  csrf_token,
  use_existing,
  existing_payment_methods,
  use_payment_intent,
  data,
  purchase_amount
}: CardFormProps) {
  return (
    <Elements stripe={getStripe()}>
      <CardFormElements
        confirm={confirm}
        orders={orders}
        isTrial={isTrial}
        current_user={current_user}
        plans={plans}
        csrf_token={csrf_token}
        use_existing={use_existing}
        existing_payment_methods={existing_payment_methods}
        use_payment_intent={use_payment_intent}
        data={data}
        purchase_amount={purchase_amount}
      />
    </Elements>
  )
}

function CardFormElements({
  confirm,
  orders,
  isTrial,
  current_user,
  plans,
  csrf_token,
  use_existing,
  existing_payment_methods,
  use_payment_intent,
  data,
  purchase_amount
}: CardFormProps) {
  const stripe = useStripe()
  const elements = useElements()

  const [isLoading, setLoading] = React.useState(false)
  const [cardComplete, setCardComplete] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [paymentMethod, setPaymentMethod] = React.useState<string>(use_existing || 'new')
  const [metadata, setMetadata] = React.useState<{ [key: string]: any }>({})

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return
    }

    setLoading(true)

    if (paymentMethod === 'new') {
      const result = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement)!,
        billing_details: {
          email: current_user.email,
        },
      })

      setLoading(false)

      if (result.error) {
        setError(result.error.message || 'An error occurred')
      } else {
        setError(null)
        stripeTokenHandler(result.paymentMethod)
      }
    } else {
      // Use existing payment method
      stripeTokenHandler({ id: paymentMethod })
    }
  }

  const stripeTokenHandler = (paymentMethod: any) => {
    // Insert the token ID into the form so it gets submitted to the server
    const form = document.getElementById('payment-form') as HTMLFormElement
    const hiddenInput = document.createElement('input')
    hiddenInput.setAttribute('type', 'hidden')
    hiddenInput.setAttribute('name', 'payment_method_id')
    hiddenInput.setAttribute('value', paymentMethod.id)
    form.appendChild(hiddenInput)

    // Add metadata
    Object.keys(metadata).forEach(key => {
      const hiddenInput = document.createElement('input')
      hiddenInput.setAttribute('type', 'hidden')
      hiddenInput.setAttribute('name', `metadata[${key}]`)
      hiddenInput.setAttribute('value', metadata[key])
      form.appendChild(hiddenInput)
    })

    // Submit the form
    form.submit()
  }

  const handleCardChange = (event: any) => {
    setCardComplete(event.complete)
    setError(event.error ? event.error.message : null)
  }

  const handlePaymentMethodChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setPaymentMethod(event.target.value)
  }

  // Add metadata based on orders
  React.useEffect(() => {
    const newMetadata: { [key: string]: any } = {}

    if (orders && orders.length > 0) {
      orders.forEach((order, index) => {
        newMetadata[`order_${index}_id`] = order.id
        newMetadata[`order_${index}_type`] = order.type
      })
    }

    if (data) {
      Object.keys(data).forEach(key => {
        newMetadata[key] = data[key]
      })
    }

    setMetadata(newMetadata)
  }, [orders, data])

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <input type="hidden" name="authenticity_token" value={csrf_token} />
      <input type="hidden" name="use_payment_intent" value={use_payment_intent.toString()} />
      <input type="hidden" name="purchase_amount" value={purchase_amount} />

      {existing_payment_methods && existing_payment_methods.length > 0 && (
        <div className="form-group">
          <label htmlFor="payment_method_select">Payment Method</label>
          <select
            id="payment_method_select"
            className="form-control"
            value={paymentMethod}
            onChange={handlePaymentMethodChange}
          >
            <option value="new">Use new card</option>
            {existing_payment_methods.map((pm) => (
              <option key={pm.id} value={pm.id}>
                **** **** **** {pm.card.last4} ({pm.card.brand.toUpperCase()})
                {pm.card.exp_month}/{pm.card.exp_year}
              </option>
            ))}
          </select>
        </div>
      )}

      {paymentMethod === 'new' && (
        <div className="form-group">
          <label htmlFor="card-element">Credit or debit card</label>
          <CardElement
            id="card-element"
            options={CardElementOptions}
            onChange={handleCardChange}
          />
        </div>
      )}

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {confirm && (
        <div className="form-group">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="confirm-checkbox"
              required
            />
            <label className="form-check-label" htmlFor="confirm-checkbox">
              I confirm that I want to purchase for ${purchase_amount}
              {!isTrial && orders && orders.length > 0 && (
                <span> for {orders.map(o => o.name).join(', ')}</span>
              )}
            </label>
          </div>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isLoading || (paymentMethod === 'new' && !cardComplete)}
        className="btn btn-primary"
      >
        {isLoading ? 'Processing...' : `Pay $${purchase_amount}`}
      </button>

    </form>
  )
}
